import { Col, Row } from "reactstrap";

//Common
import {
    Chip,
    ConfirmationDialog,
    DataGrid,
    Drawer,
    Snackbar,
} from "../../common/VFX-UI";
import PageHeader from "../../common/PageHeader/PageHeader";
import MoreActionsMenu from "../../common/MoreActionsMenu/MoreActionsMenu";
import { MoreActionButton } from "../../common/Buttons";

//App
import { CountyFormContainer } from "./CountyFormContainer";
import useComponentGeneralStyles from "../../styles";
import { FormikTypeaheadOption } from "../../../types";
import CountyView from "./CountyView";
import useCounty from "./hooks/useCounty";
import { CountyProps } from "../../../types/county";
import { SearchProps } from "../../../types";
import { SearchDefaultValue } from "../../../store/types";
import { CountyDto } from "../../../app/model/api/types/dto";
import { SearchFieldsDefaultValue } from "../../../utility/constants/constants";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";

const County: React.FC<CountyProps> = (props) => {
    const { data: counties, activateMutation, deleteMutation } = props;
    const generalStyles = useComponentGeneralStyles();

    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleDeactivateHandler,
        toggleActivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useCounty({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    const handleOnStateChange = async (value: FormikTypeaheadOption[]) => {
        let stateId: string | null = null;

        if (value && value?.length > 0) {
            stateId = (value[0] as Record<string, string | number>)[
                "value"
            ] as string;
        }
        await formContext?.setStateId(stateId);
    };

    const searchProps: SearchProps<CountyDto> = {
        dropdown: {options: formContext?.states ?? [], defaultSelected: formContext?.stateId != null ? [formContext.stateId] : [], 
            clearButton: true, name: "stateId", label: "State", onChange: (value:any) =>handleOnStateChange(value) },
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: { ...SearchFieldsDefaultValue, name: true },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };

    const columns = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },
        {
            dataField: "description",
            text: "Description",
            sort: true,
        },
        {
            dataField: "state",
            text: "State",
            sort: true,
        },
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean) => {
                return <Chip label={cell ? "Active" : "Inactive"} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ];

    return (
        <>
            <PageHeader>County</PageHeader>
            <div style={generalStyles.searchContainer}>
                <Row style={generalStyles.width100}>
                    <Col sm={12}>
                        <SearchContainer
                            searchProps={searchProps}
                            activeToggle={formContext?.activeToggle ?? true}
                            handleActiveToggleChange={handleActiveToggleChange}
                            toggleNewViewHandler={
                                formContext?.stateId !== null ? toggleNewViewHandler : undefined
                            }
                        />
                    </Col>
                </Row>
            </div>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <CountyFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <CountyFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <CountyView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <DataGrid
                columns={columns}
                data={counties}
                keyField="id"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                page={formContext?.paging?.currentPage}
                pageSize={formContext?.pageSize}
                remote={true}
                handlePageChange={async (newPage, pageSize ,sortField, sortOrder) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    if (formContext?.setSortBy && sortField && sortOrder) {
                        await formContext?.setSortBy({
                            field: sortField,
                            direction: sortOrder === "asc" ? 1 : -1,
                        });
                    }
                    formContext?.refetchGrid();
                }}
            />

            {actionColumnAnchorEl && (
                <MoreActionsMenu
                    isOpen={openMenu}
                    target={actionColumnAnchorEl}
                    toggle={() => {
                        setActionColumnAnchorEl(null);
                    }}
                    onViewClick={() => {
                        toggleViewHandler();
                        setActionColumnAnchorEl(null);
                    }}
                />
            )}

            <ConfirmationDialog
                open={state.isDeactivateDialogOpen}
                onClose={toggleDeactivateHandler}
                onConfirm={handleDeactivate}
                contentTitle={`Deactivate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>

            <ConfirmationDialog
                open={state.isActivateDialogOpen}
                onClose={toggleActivateHandler}
                onConfirm={handleActivate}
                contentTitle={`Activate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            />

            {state.showSuccessSnackbar && (
                <Snackbar
                    open={state.showSuccessSnackbar}
                    message={state.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default County;
